import { api } from './client';

// ── V47 — workspace SMTP credentials (Connect Email) ──────────────────────

export interface WorkspaceSmtpStatus {
  configured: boolean;
  host: string | null;
  port: number | null;
  username: string | null;
  fromAddress: string | null;
  fromName: string | null;
  connectedAt: string | null;
}

export interface UpdateWorkspaceSmtpRequest {
  host?: string;
  port?: number;
  username: string;
  /** Only required on first save. Omit when editing the from-name. */
  password?: string;
  fromAddress?: string;
  fromName?: string;
}

export interface TestWorkspaceSmtpResponse {
  ok: boolean;
  error: string | null;
}

// ── Scheduled email reports (V128, Tier 2.4) ──────────────────────────
//
// Recurring weekly/monthly PDF analytics emailed to a recipient list.
// Backend lives at /api/workspaces/{id}/report-schedules — owner-only,
// gated on FeatureFlag.SCHEDULED_REPORTS.
export interface ReportSchedule {
  id: string;
  workspaceId: string;
  brandId: string | null;
  brandName: string | null;
  reportType: string;
  frequency: 'weekly' | 'monthly';
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  sendHourUtc: number;
  recipients: string[];
  enabled: boolean;
  lastSentAt: string | null;
  lastError: string | null;
  createdAt: string | null;
}

export interface CreateReportScheduleRequest {
  brandId?: string | null;
  reportType?: string;
  frequency: 'weekly' | 'monthly';
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  sendHourUtc?: number;
  recipients: string[];
}

export interface UpdateReportScheduleRequest {
  brandId?: string | null;
  frequency?: 'weekly' | 'monthly';
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  sendHourUtc?: number;
  recipients?: string[];
  enabled?: boolean;
}

// ── Auto-responses (V130, Tier 2.6) ───────────────────────────────────
//
// Rule-based comment / mention auto-replies. Owner-only on the
// backend, plan-gated on FeatureFlag.AUTO_RESPONSES (Trial+Pro+Agency).
export interface AutoResponseRule {
  id: string;
  workspaceId: string;
  brandId: string | null;
  brandName: string | null;
  name: string;
  enabled: boolean;
  triggerType: 'keyword' | 'sentiment' | 'all';
  triggerValue: string | null;
  platforms: string[];
  itemTypes: string[];
  responseMode: 'template' | 'ai_suggestion';
  responseText: string | null;
  requireReview: boolean;
  maxPerHour: number;
  repliesSent: number;
  lastRepliedAt: string | null;
  createdAt: string | null;
}

export interface CreateAutoResponseRuleRequest {
  brandId?: string | null;
  name: string;
  enabled?: boolean;
  triggerType: 'keyword' | 'sentiment' | 'all';
  triggerValue?: string | null;
  platforms?: string[];
  itemTypes?: string[];
  responseMode?: 'template' | 'ai_suggestion';
  responseText?: string | null;
  requireReview?: boolean;
  maxPerHour?: number;
}

export interface UpdateAutoResponseRuleRequest {
  brandId?: string | null;
  name?: string;
  enabled?: boolean;
  triggerType?: 'keyword' | 'sentiment' | 'all';
  triggerValue?: string | null;
  platforms?: string[];
  itemTypes?: string[];
  responseMode?: 'template' | 'ai_suggestion';
  responseText?: string | null;
  requireReview?: boolean;
  maxPerHour?: number;
}

export const autoResponseApi = {
  list: (workspaceId: string) =>
    api
      .get<AutoResponseRule[]>(`/api/workspaces/${workspaceId}/auto-responses`)
      .then((r) => r.data),
  create: (workspaceId: string, body: CreateAutoResponseRuleRequest) =>
    api
      .post<AutoResponseRule>(`/api/workspaces/${workspaceId}/auto-responses`, body)
      .then((r) => r.data),
  update: (workspaceId: string, ruleId: string, body: UpdateAutoResponseRuleRequest) =>
    api
      .patch<AutoResponseRule>(`/api/workspaces/${workspaceId}/auto-responses/${ruleId}`, body)
      .then((r) => r.data),
  delete: (workspaceId: string, ruleId: string) =>
    api.delete(`/api/workspaces/${workspaceId}/auto-responses/${ruleId}`).then((r) => r.data),
};

export const reportScheduleApi = {
  list: (workspaceId: string) =>
    api
      .get<ReportSchedule[]>(`/api/workspaces/${workspaceId}/report-schedules`)
      .then((r) => r.data),
  create: (workspaceId: string, body: CreateReportScheduleRequest) =>
    api
      .post<ReportSchedule>(`/api/workspaces/${workspaceId}/report-schedules`, body)
      .then((r) => r.data),
  update: (workspaceId: string, scheduleId: string, body: UpdateReportScheduleRequest) =>
    api
      .put<ReportSchedule>(`/api/workspaces/${workspaceId}/report-schedules/${scheduleId}`, body)
      .then((r) => r.data),
  delete: (workspaceId: string, scheduleId: string) =>
    api.delete(`/api/workspaces/${workspaceId}/report-schedules/${scheduleId}`).then((r) => r.data),
  sendNow: (workspaceId: string, scheduleId: string) =>
    api
      .post<{
        status: string;
      }>(`/api/workspaces/${workspaceId}/report-schedules/${scheduleId}/send-now`)
      .then((r) => r.data),
};

export const workspaceSmtpApi = {
  get: (workspaceId: string) =>
    api.get<WorkspaceSmtpStatus>(`/api/workspaces/${workspaceId}/smtp`).then((r) => r.data),
  update: (workspaceId: string, body: UpdateWorkspaceSmtpRequest) =>
    api.put<WorkspaceSmtpStatus>(`/api/workspaces/${workspaceId}/smtp`, body).then((r) => r.data),
  delete: (workspaceId: string) =>
    api.delete<WorkspaceSmtpStatus>(`/api/workspaces/${workspaceId}/smtp`).then((r) => r.data),
  test: (workspaceId: string) =>
    api
      .post<TestWorkspaceSmtpResponse>(`/api/workspaces/${workspaceId}/smtp/test`, {})
      .then((r) => r.data),
};

// ── Admin global SMTP (system_settings.smtp.*) ────────────────────────────
//
// Replaces the old spring.mail.* env-var config. EmailService.send() reads
// this as the global sender; per-workspace SMTP (above) takes precedence
// for workspace-bound mail like invitations.

export interface AdminSmtpStatus {
  configured: boolean;
  host: string | null;
  port: number | null;
  username: string | null;
  fromAddress: string | null;
  fromName: string | null;
  connectedAt: string | null;
}

export interface UpdateAdminSmtpRequest {
  host?: string;
  port?: number;
  username: string;
  /** Required on first save. Omit when editing other fields. */
  password?: string;
  fromAddress?: string;
  fromName?: string;
}

export interface TestAdminSmtpResponse {
  ok: boolean;
  error: string | null;
}

export const adminEmailApi = {
  get: () => api.get<AdminSmtpStatus>('/api/admin/email/smtp').then((r) => r.data),
  update: (body: UpdateAdminSmtpRequest) =>
    api.put<AdminSmtpStatus>('/api/admin/email/smtp', body).then((r) => r.data),
  delete: () => api.delete<AdminSmtpStatus>('/api/admin/email/smtp').then((r) => r.data),
  test: () => api.post<TestAdminSmtpResponse>('/api/admin/email/smtp/test', {}).then((r) => r.data),
  sendTest: () =>
    api.post<TestAdminSmtpResponse>('/api/admin/email/smtp/send-test', {}).then((r) => r.data),
};
